import React from 'react';
import { useJournal } from '../../context/JournalContext';
import { cn } from '../../lib/cn';

/** Neutral chip styles — tags are never colour-coded. */
const CHIP = {
  background: '#f4f5f7',
  borderColor: '#e2e4e8',
  color: '#4f5560'
};
const CHIP_HOVER = {
  background: '#f3f4f6',
  borderColor: '#d8dbe0',
  color: '#4f5560'
};
const CHIP_ON = {
  background: '#e6e8ec',
  borderColor: '#6b7280',
  color: '#1f2937'
};

/**
 * Strategy confirmations, built dynamically from the user's own categories and
 * numbered so the flow reads 1. Dirección → 2. Zona → 3. Entrada.
 */
export function JournalTagFields({
  value,
  onChange
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const {
    categoriesSorted,
    tagsByCategory
  } = useJournal();
  const toggle = (id: string, categoryId: string, multiple: boolean) => {
    const inCategory = tagsByCategory(categoryId).map(tag => tag.id);
    if (value.includes(id)) return onChange(value.filter(selected => selected !== id));
    const withoutCategory = multiple ? value : value.filter(selected => !inCategory.includes(selected));
    onChange([...withoutCategory, id]);
  };
  const categories = categoriesSorted.filter(category => tagsByCategory(category.id).length > 0);
  if (categories.length === 0) {
    return <p className="text-[13px] text-ink-soft" data-id="element-331" data-mp="1t37gaoakjqd9">
        No hay etiquetas activas. Créalas en la pestaña Etiquetas.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-id="element-332" data-mp="2lql3huogt7k6">
      {categories.map((category, index) => <div key={category.id} className="rounded-xl border border-line bg-surface/35 p-3" data-id="element-333" data-mp="2qnlcoegm2qfi" data-mpi={index}>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold" style={{
        color: '#24272c'
      }} data-id="element-334" data-mp="2byp5skqajjgh">
            <span className="flex items-center justify-center text-[10px] font-semibold" style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#f0f1f3',
          color: '#4f5560'
        }} data-id="element-335" data-mp="2gxiiwc1o18g4">
              {index + 1}
            </span>
            {category.name}
            {category.timeframe && <span className="font-medium text-ink-soft" data-id="element-336" data-mp="3jabi4a5zj188">— {category.timeframe}</span>}
          </p>
          <p className="mt-0.5 text-[10px] text-ink-soft" data-id="element-337" data-mp="3ect6gc0l27wj">
            {category.multiple ? 'Selección múltiple' : 'Selección única'}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-338" data-mp="35bv4dz9pgagl">
            {tagsByCategory(category.id).map(tag => <TagChip key={tag.id} label={tag.label} selected={value.includes(tag.id)} onClick={() => toggle(tag.id, category.id, !!category.multiple)} data-id="element-339" data-mp="30dad4pcfl14a" />)}
          </div>
        </div>)}
    </div>;
}
function TagChip({
  label,
  selected,
  onClick
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const tone = selected ? CHIP_ON : hover ? CHIP_HOVER : CHIP;
  return <button type="button" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} aria-pressed={selected} className={cn('rounded-full border px-2.5 py-1 text-[11px] transition-all duration-150', selected ? 'font-semibold' : 'font-medium')} style={{
    backgroundColor: tone.background,
    borderColor: tone.borderColor,
    color: tone.color
  }} data-id="element-340" data-mp="2cng0o8w0xekk">
      {label}
    </button>;
}